/**
 * INPUT.
 *
 * Physical key codes, never `event.key`. `KeyW` is the same physical key on
 * QWERTY, AZERTY and Dvorak; `"w"` is not, and a French player would otherwise
 * find that walking forward is bound to Z.
 *
 * The controller reads a snapshot of this rather than subscribing to events, so
 * the movement code stays a pure function of state and can be driven by a bot in
 * a test with no DOM at all.
 */

export interface InputState {
  forward: number;
  strafe: number;
  jump: boolean;
  sprint: boolean;
  crouch: boolean;
  /** Accumulated mouse delta since the last read, in radians. */
  yawDelta: number;
  pitchDelta: number;
}

/** Edge-triggered actions. Drained once per frame so a tap is never missed. */
export type Action =
  | 'interact'
  | 'cancel'
  | 'drop'
  | 'dropAll'
  | 'use'
  | 'scoreboard'
  | 'map'
  | 'slot1'
  | 'slot2'
  /** Barge a guard over from behind. The thief's only physical answer. */
  | 'shove'
  /** Close the microphone. Local only; nobody else is told. */
  | 'mute';

const HELD_BINDINGS: Record<string, keyof InputState | undefined> = {};

/** Radians of look per pixel of mouse movement at sensitivity 1. */
const LOOK_SCALE = 0.0022;
const PITCH_LIMIT = Math.PI / 2 - 0.02;

export class Input {
  readonly state: InputState = {
    forward: 0,
    strafe: 0,
    jump: false,
    sprint: false,
    crouch: false,
    yawDelta: 0,
    pitchDelta: 0,
  };

  sensitivity = 1;
  invertY = false;
  /** True while the pointer is captured; the controller ignores look otherwise. */
  locked = false;

  private readonly down = new Set<string>();
  private readonly queue: Action[] = [];
  private readonly held = new Set<Action>();
  private readonly detach: Array<() => void> = [];

  constructor(private readonly surface: HTMLElement) {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.repeat || typing(e)) return;
      this.down.add(e.code);
      const action = EDGE[e.code];
      if (action) {
        this.queue.push(action);
        this.held.add(action);
      }
      // Only swallow keys the game actually uses, or devtools shortcuts break.
      if (e.code in EDGE || e.code in MOVE || e.code === 'Space') e.preventDefault();
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (typing(e)) return;
      this.down.delete(e.code);
      const action = EDGE[e.code];
      if (action) this.held.delete(action);
    };
    const onMove = (e: MouseEvent) => {
      if (!this.locked) return;
      this.state.yawDelta -= e.movementX * LOOK_SCALE * this.sensitivity;
      this.state.pitchDelta += (this.invertY ? 1 : -1) * e.movementY * LOOK_SCALE * this.sensitivity;
    };
    const onMouseDown = (e: MouseEvent) => {
      if (!this.locked) return;
      if (e.button === 0) {
        this.queue.push('interact');
        this.held.add('interact');
      }
    };
    const onMouseUp = (e: MouseEvent) => {
      if (e.button === 0) this.held.delete('interact');
    };
    const onLockChange = () => {
      this.locked = document.pointerLockElement === this.surface;
      if (!this.locked) {
        // Releasing the pointer must release every key too, or alt-tabbing while
        // sprinting leaves the player running into a wall forever.
        this.down.clear();
        this.held.clear();
      }
    };
    const onBlur = () => {
      this.down.clear();
      this.held.clear();
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('blur', onBlur);
    document.addEventListener('pointerlockchange', onLockChange);

    this.detach.push(
      () => window.removeEventListener('keydown', onKeyDown),
      () => window.removeEventListener('keyup', onKeyUp),
      () => window.removeEventListener('mousemove', onMove),
      () => window.removeEventListener('mousedown', onMouseDown),
      () => window.removeEventListener('mouseup', onMouseUp),
      () => window.removeEventListener('blur', onBlur),
      () => document.removeEventListener('pointerlockchange', onLockChange),
    );
    void HELD_BINDINGS;
  }

  requestLock(): void {
    void this.surface.requestPointerLock();
  }

  releaseLock(): void {
    if (document.pointerLockElement === this.surface) document.exitPointerLock();
  }

  /** Folds held keys into the movement axes. Call once per frame, before reading. */
  sample(): InputState {
    const s = this.state;
    s.forward = (this.down.has('KeyW') ? 1 : 0) - (this.down.has('KeyS') ? 1 : 0);
    s.strafe = (this.down.has('KeyD') ? 1 : 0) - (this.down.has('KeyA') ? 1 : 0);
    s.jump = this.down.has('Space');
    s.sprint = this.down.has('ShiftLeft') || this.down.has('ShiftRight');
    s.crouch = this.down.has('ControlLeft') || this.down.has('KeyC');
    return s;
  }

  /** Consumes the accumulated look delta, clamped so a flick cannot teleport. */
  takeLook(): { yaw: number; pitch: number } {
    const yaw = clamp(this.state.yawDelta, -1.2, 1.2);
    const pitch = clamp(this.state.pitchDelta, -1.2, 1.2);
    this.state.yawDelta = 0;
    this.state.pitchDelta = 0;
    return { yaw, pitch };
  }

  drainActions(): Action[] {
    const out = this.queue.slice();
    this.queue.length = 0;
    return out;
  }

  /**
   * Queue an action as if the key had been tapped.
   *
   * The bot client's way in. It goes through the same queue a real keypress
   * does, so a test that drops a tool exercises the identical path — a test that
   * called the session's handler directly would keep passing after the keybind
   * was deleted.
   */
  inject(a: Action): void {
    this.queue.push(a);
  }

  isHeld(a: Action): boolean {
    return this.held.has(a);
  }

  dispose(): void {
    for (const d of this.detach) d();
  }
}

const EDGE: Record<string, Action | undefined> = {
  KeyE: 'interact',
  KeyF: 'use',
  KeyG: 'drop',
  KeyQ: 'dropAll',
  Escape: 'cancel',
  Tab: 'scoreboard',
  KeyM: 'map',
  // V, because R restarts the round from the results screen and everything
  // nearer the movement keys is already spoken for.
  KeyV: 'shove',
  // Next to the movement keys and nowhere near anything destructive, because it
  // is the one key people reach for in a hurry while already running away.
  KeyB: 'mute',
  Digit1: 'slot1',
  Digit2: 'slot2',
};

const MOVE: Record<string, true> = {
  KeyW: true,
  KeyA: true,
  KeyS: true,
  KeyD: true,
};

export const PITCH_CLAMP = PITCH_LIMIT;

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/**
 * A key typed into a text field is not a movement key.
 *
 * The listeners are on `window` so that the game keeps responding when focus
 * wanders, which is right for a pointer-locked first-person view and wrong the
 * moment there is an input box on screen — the lobby's name field would swallow
 * a W and move the player instead, and `preventDefault` on the movement keys
 * would stop the letter being typed at all.
 */
function typing(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null;
  return !!t && (t.isContentEditable === true || t.tagName === 'INPUT' || t.tagName === 'TEXTAREA');
}
